import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import BASE_URL from "../config";

const EditPage = () => {
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const id = query.get("id");

  const [page, setPage] = useState({
    title: "",
    pagename: "",
    slug: "",
    content: "",
    category: "",
    parent_id: "",
    meta: "",
    active: false,
    modifiedat: new Date().toISOString().split("T")[0],
  });

  const [categories, setCategories] = useState([]); // Store categories
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({}); // Store validation errors

  // Fetch page data
  useEffect(() => {
    if (!id) {
      Swal.fire("Error", "Page ID is missing", "error");
      navigate("/pages");
      return;
    }

    const fetchPage = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${BASE_URL}api/pages/find-one/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data) {
          setPage(response.data);
        } else {
          throw new Error("No data received");
        }
      } catch (error) {
        Swal.fire("Error", error.response?.data?.message || "Failed to fetch page details", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [id, navigate]);

  // Fetch categories (only those with no parent_id)
  useEffect(() => {
    const fetchPages = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${BASE_URL}api/pages/find-all`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data) {
          const filteredCategories = response.data.filter((category) => !category.parent_id);
          setCategories(filteredCategories);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchPages();
  }, []);

  // Handle input change and remove error on valid input
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPage((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));

    // Remove error when user corrects input
    if (value.trim()) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    }
  };

  // Validate form fields
  const validateForm = () => {
    let newErrors = {};
    // Use (page.field ?? "") to avoid null/undefined errors
    if (!(page.pagename ?? "").trim()) newErrors.pagename = "Page name is required.";
    if (!(page.slug ?? "").trim()) newErrors.slug = "Slug is required.";
    // if (!(page.title ?? "").trim()) newErrors.title = "Title is required.";
    if (!(page.content ?? "").trim()) newErrors.content = "Content is required.";
    // if (!(page.meta ?? "").trim()) newErrors.meta = "Meta description is required.";
    if (!(page.category ?? "").trim()) newErrors.category = "Category Selection is required.";
    // if (!(page.parent_id ?? "").trim()) newErrors.parent_id = "Parent Selection is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const token = localStorage.getItem("token");
      const updatedPage = { ...page, modifiedat: new Date().toISOString().split("T")[0] };

      await axios.put(`${BASE_URL}api/pages/update/${id}`, updatedPage, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });

      Swal.fire("Success", "Page updated successfully", "success").then(() => navigate("/pages"));
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Failed to update page", "error");
    }
  };

  return (
    <div className="container mt-4">
      <h2 className="text-primary">Edit Page</h2>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Page Name</label>
              <input type="text" className={`form-control ${errors.pagename && "border border-danger"}`} name="pagename" value={page.pagename} onChange={handleChange} />
              {errors.pagename && <div className="text-danger">{errors.pagename}</div>}
            </div>
            <div className="col-md-6">
              <label className="form-label">Slug</label>
              <input type="text" className={`form-control ${errors.slug && "border border-danger"}`} name="slug" value={page.slug} onChange={handleChange} />
              {errors.slug && <div className="text-danger">{errors.slug}</div>}
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Title</label>
              <input type="text" className={`form-control ${errors.title && "border border-danger"}`} name="title" value={page.title} onChange={handleChange} />
              {errors.title && <div className="text-danger">{errors.title}</div>}
            </div>
            <div className="col-md-6">
              <label className="form-label">Category</label>
              <select className={`form-control ${errors.category && "border border-danger"}`} name="category" value={page.category} onChange={handleChange}>
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.pagename}>
                    {cat.pagename}
                  </option>
                ))}
              </select>
              {errors.category && <div className="text-danger">{errors.category}</div>}
            </div>
          </div>
          <div className="col-md-6">
              <label className="form-label">Parent</label>
              <select className={`form-control ${errors.parent_id && "border border-danger"}`} name="parent_id" value={page.parent_id} onChange={handleChange}>
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.pagename}>
                    {cat.pagename}
                  </option>
                ))}
              </select>
              {errors.parent_id && <div className="text-danger">{errors.parent_id}</div>}
            </div>
          <div className="mb-3">
            <label className="form-label">Content</label>
            <textarea className={`form-control ${errors.content && "border border-danger"}`} name="content" rows="2" value={page.content} onChange={handleChange}></textarea>
            {errors.content && <div className="text-danger">{errors.content}</div>}
          </div>

          <div className="mb-3">
            <label className="form-label">Meta</label>
            <textarea className={`form-control ${errors.meta && "border border-danger"}`} name="meta" rows="3" value={page.meta} onChange={handleChange}></textarea>
            {errors.meta && <div className="text-danger">{errors.meta}</div>}
          </div>

          <button type="submit" className="btn btn-primary me-1">Update Page</button>
          <a href="/pages" className="btn btn-secondary ms-1">Cancel</a>
        </form>
      )}
    </div>
  );
};

export default EditPage;
